import { EdgeDocumentsApi } from '@trialcard/apigateway.client';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { Mutation, Action } from 'vuex-module-decorators';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import EligibilityModule from './eligibility';
import { Settings } from '~/services/settings';
import EnrollmentModule from '~/store/enrollment';
import { trackEvent, trackException } from '~/services/telemetry';
interface IUploadValidation {
    isInvalidType: boolean | null;
    isInvalidSize: boolean | null;
}

@InjectModule({ stateFactory: true }, module)
export default class DocumentUploadModule extends InjectVuexModule {
    @Resolve
    public settings!: Settings;

    @Resolve
    documentApi!: EdgeDocumentsApi;

    @Resolve
    public eligibility!: EligibilityModule;

    @Resolve
    public enrollment!: EnrollmentModule;

    public files: VUFile[] = [];
    public validFileTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    public maxFileSize = 10000000;
    public pendingUploadSize = 0;
    public totalUploadSize = 0;
    // validation state of incoming upload
    public uploadValidation: IUploadValidation = {
        isInvalidSize: null,
        isInvalidType: null,
    };
    // validation error messages
    public isInvalidSizeError = false;
    public isInvalidTypeError = false;
    public buttonID = 'direct_upload';
    
    @Mutation
    public validateUpload(data: VUFile) {
        const isInvalidSize = data.file.size + this.pendingUploadSize + this.totalUploadSize > this.maxFileSize;
        const isInvalidType = !this.validFileTypes.includes(data.file.type);
        if (!isInvalidSize) {
            this.pendingUploadSize += data.file.size;
        }
        this.uploadValidation = {
            isInvalidSize,
            isInvalidType,
        };
    }

    @Mutation
    setIsInvalidSizeError(value: boolean) {
        this.isInvalidSizeError = value;
    }

    @Mutation
    setIsInvalidTypeError(value: boolean) {
        this.isInvalidTypeError = value;
    }

    @Mutation
    public addFile(data: VUFile) {
        this.files.push(data);
        this.totalUploadSize += data.file.size;
    }

    @Mutation
    public removeFile(data: VUFile) {
        const index = this.files.findIndex(file => file.id === data.id);
        if (index !== -1) {
            this.files.splice(index, 1);
            this.totalUploadSize -= data.file.size;
        }

        if (this.totalUploadSize <= this.maxFileSize) {
            this.isInvalidSizeError = false;
        }

        if (this.files.length === 0) {
            this.isInvalidTypeError = false;
        }
    }

    @Mutation
    public clearPendingUploads() {
        this.pendingUploadSize = 0;
    }
    @Mutation
    public setButtonID(buttonID: 'direct_upload' | 'enrollment_upload') {
        this.buttonID = buttonID;
    }

    @Mutation
    public resetUploadValidation() {
        this.uploadValidation = {
            isInvalidSize: null,
            isInvalidType: null,
        };
    }

    @Mutation
    public clearStore() {
        this.files = [];
        this.uploadValidation = {
            isInvalidSize: null,
            isInvalidType: null,
        };
        this.isInvalidSizeError = false;
        this.isInvalidTypeError = false;
        this.pendingUploadSize = 0;
        this.totalUploadSize = 0;
    }

   @Action({})
    async uploadDocument(request: { file: File; documentTypeId: string | null; programId?: string }) {
        const startedAt = Date.now();
        // Tracks whether we actually got as far as issuing the HTTP request. This
        // lets telemetry distinguish "call was never sent" (auth/setup threw) from
        // "call was sent but failed" (server/network error).
        let requestIssued = false;
        let programIdForLog: string | undefined;
        const sizeBytes = request.file ? request.file.size : 0;
        try {
            // `this.settings` (vue-di @Resolve) can resolve to undefined inside
            // this vuex action depending on how the caller obtained the module.
            // Reading a property off it was throwing here and silently abandoning
            // the upload (surfaced as DocumentUpload.RequestNeverSent). Callers
            // that have reliable settings access (enrollment components, via
            // this.$settings) pass `programId` directly; we only fall back to
            // deriving it from this.settings when they don't, and guard for the
            // undefined case so the upload is never abandoned on this line.
            const settings = this.settings;

            let programId = request.programId;
            if (!programId) {
                const values = (this.eligibility && this.eligibility.eligibilityData) || [];

                const purchasePrescriptionAnswer =
                    values.length > 1 && values[1] && values[1].answerId
                        ? values[1].answerId.toString()
                        : '';

                const isGovernmentSponsored =
                    (this.eligibility &&
                        this.eligibility.prescriptionInsuranceType === 'government-sponsored') ||
                    (!!settings &&
                        purchasePrescriptionAnswer ===
                            settings.eligibilityPurchasePrescriptionAnswerIdGovernment);

                if (settings) {
                    programId = isGovernmentSponsored
                        ? settings.governmentSponsoredProgramId!
                        : settings.uninsuredProgramId!;
                }
            }
            programIdForLog = programId;

            if (this.enrollment.testPDF) {
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(request.file);
                link.target = '_blank';
                link.click();
            }

            const form = new FormData();

            if (request.documentTypeId) {
                form.append('type', request.documentTypeId);
            }

            form.append('attachment', request.file);

            const axios: AxiosInstance = (this.documentApi as any).axios;

            const headers: Record<string, string> = {};
            if (programId) {
                headers['x-program-id'] = programId;
            }

            const axiosRequest: AxiosRequestConfig = {
                url: 'edge/documents/v1/Document/upload',
                method: 'POST',
                data: form,
                headers,
            };

            // The documents API only requires the Token auth scheme (same as the
            // enroll call). Applying ApiKey/Bearer as well was an extra failure
            // surface that could throw and prevent the request from ever being
            // sent, so they have been removed.
            await (this.documentApi as any).authentications.Token.applyToRequest(axiosRequest);

            trackEvent('DocumentUpload.RequestSending', {
                programId: programIdForLog,
                buttonID: this.buttonID,
                sizeBytes,
            });
            requestIssued = true;

            // Retry transient upload failures (network blips, brief 5xx). The
            // enrollment continues regardless of the outcome, so the only cost of
            // a retry is a little latency on the failing path.
            const maxAttempts = 3;
            let lastError: unknown;
            for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                try {
                    const { data } = await axios.request(axiosRequest);
                    const success = !!(data && data.data && data.success);
                    trackEvent('DocumentUpload.RequestCompleted', {
                        programId: programIdForLog,
                        buttonID: this.buttonID,
                        sizeBytes,
                        success,
                        attempt,
                        durationMs: Date.now() - startedAt,
                    });
                    return success;
                } catch (error) {
                    lastError = error;
                    if (attempt < maxAttempts) {
                        await new Promise(resolve => setTimeout(resolve, 400 * attempt));
                    }
                }
            }
            throw lastError;

        } catch (error) {
            const err = error as any;
            const status = err && err.response ? err.response.status : undefined;
            // requestIssued === false means the /Document/upload call was never
            // sent (e.g. auth application or PDF/setup threw first). This is the
            // "no upload log at all, but enrollment still succeeds" case.
            const eventName = requestIssued
                ? 'DocumentUpload.RequestFailed'
                : 'DocumentUpload.RequestNeverSent';
            trackException(error, {
                event: eventName,
                programId: programIdForLog,
                buttonID: this.buttonID,
                sizeBytes,
                httpStatus: status,
                durationMs: Date.now() - startedAt,
            });
            trackEvent(eventName, {
                programId: programIdForLog,
                buttonID: this.buttonID,
                sizeBytes,
                httpStatus: status,
                durationMs: Date.now() - startedAt,
            });
            return false;
        }
    }
}